import { ForwardRefRenderFunction, useRef } from 'react';
import {
	Flex,
	FormControl,
	FormErrorMessage,
	FormLabel,
	forwardRef,
	Icon,
	Input as ChakraInput,
	InputProps as ChakraInputProps,
	Text,
  InputGroup,
  InputLeftElement,
  InputRightElement
} from '@chakra-ui/react';
import { FieldError } from 'react-hook-form';
import { IconBaseProps } from 'react-icons';

interface InputProps extends ChakraInputProps {
	name: string;
	label?: string;
	iconLabel?: React.ComponentType<IconBaseProps>;
  iconInput?: React.ComponentType<IconBaseProps>;
  iconInputRight?: {
    icon: React.ComponentType<IconBaseProps>;
    showPassword: boolean;
    setShowPassword: (show: boolean) => void;
  };
	error?: FieldError;
}

const InputBase: ForwardRefRenderFunction<HTMLInputElement, InputProps> = (
  { name, label, iconLabel, iconInput, iconInputRight, error = null, ...rest },
  ref
) => {
  const inputRef = useRef<HTMLInputElement>(null);

	return (
    <FormControl isInvalid={!!error} _focus={{ colorScheme: "blue.500" }} ref={ref}>
      <FormLabel htmlFor={name} pb={0} mb={1} fontWeight="400"
        display={rest.display}
        visibility={rest.visibility}
      >
        <Flex align="center">
          {iconLabel && <Icon as={iconLabel} fontSize={18} mr={2} />}
          {label && <Text fontSize={[16, 18]}>{label}</Text>}
        </Flex>
      </FormLabel>

      <InputGroup>
        {iconInput && (
          <InputLeftElement h="100%" _focus={{ color: "blue.500" }} >
            <Icon as={iconInput} fontSize="lg" />
          </InputLeftElement>
        )}

        <ChakraInput
          id={name}
          name={name}
          pl={10}
          borderWidth={1}
          borderColor="gray.300"
          focusBorderColor="blue.500"
          variant="outline"
          rounded={8}
          bgColor="white"
          _hover={{
            bgColor: 'gray.100'
          }}
          _focus={{
            bgColor: 'white'
          }}
          size="lg"
          ref={inputRef}
          {...rest}
          autoFocus={true}
        />

        {iconInputRight && (
          <InputRightElement h="100%" >
            <iconInputRight.icon
              cursor="pointer"
              onClick={(e: any) => {
                iconInputRight.setShowPassword(!iconInputRight.showPassword);

                if (inputRef.current) {
                  const inputValueSize = inputRef.current.value.length;
                  inputRef.current.setSelectionRange(inputValueSize, inputValueSize);
                  setTimeout(() => inputRef.current?.focus(), 200);
                }
              }}
            />
          </InputRightElement>
        )}
      </InputGroup>

			{!!error && (
				<FormErrorMessage fontWeight={500} mt={0.5} ml={1}>
					{error.message}
				</FormErrorMessage>
			)}
		</FormControl>
	)
}

export const Input = forwardRef(InputBase);
// export default Input;
